/**
 * Pure helpers for comparing Eva's typed attempt against the correct spelling.
 * Spec §4.2: "tapping Done records her answer (case-insensitive,
 * whitespace-trimmed)".
 *
 * Kept tiny + side-effect-free so the same rule applies in first-pass
 * scoring, second-chance scoring, and the derivation of the "missed
 * words" list that drives second-chance + 3x-write. Anything more
 * sophisticated (e.g. fuzzy/diff helpers for a visual letter diff)
 * belongs alongside this file if it ever lands — for v1 the spec
 * deliberately keeps results to a coarse correct/incorrect marker (§4.3).
 */
import type { WordEntry } from "@/types/data";

/**
 * Trim leading/trailing whitespace and lowercase for an apples-to-apples
 * comparison. Does not collapse internal whitespace — "ice cream" with
 * a double-space typed is still meant to be a miss for "ice cream".
 */
export function normaliseForCompare(value: string): string {
  return value.trim().toLowerCase();
}

/** True iff the attempt matches the target under the §4.2 rule. */
export function attemptMatches(attempt: string, target: string): boolean {
  return normaliseForCompare(attempt) === normaliseForCompare(target);
}

/**
 * Derive the ordered list of words Eva missed on first pass.
 *
 * Both second chance (§4.4) and 3x-write (§4.5) iterate this list in
 * the original first-pass order. Crucially, 3x-write does *not* care
 * about the second-chance outcome — spec §4.5: "the 3x-write phase
 * applies to **any** word missed on first pass, regardless of whether
 * she got it right on the second chance." So this single helper
 * underpins both screens; don't re-derive against second-chance state.
 */
export function missedFromFirstPass(
  words: WordEntry[],
  firstPassAttempts: Record<number, string>,
): WordEntry[] {
  return words.filter(
    (w) => !attemptMatches(firstPassAttempts[w.id] ?? "", w.spelling),
  );
}
